/**
 * Os detalhes do arquivo focado (docs/spec/desk-cloud.md, "A página"): a prévia (leva à
 * pré-visualização), o nome que se renomeia ali mesmo, Abrir, Baixar, Copiar link e Lixeira, "Usar na
 * mesa" com as ações do tipo, "Quem pode abrir" e as informações. Na lixeira: os dias que faltam,
 * Restaurar e Excluir para sempre. No celular, uma folha que sobe de baixo.
 */
import { useState } from 'react';
import { formatBytes } from '../bytes.js';
import { useLanguage, useT } from '../i18n/index.js';
import { serverUrl } from '../serverUrl.js';
import type { DeskFile, DeskRoom } from '../shared.js';
import { Button, ComboBox } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { copyLink, downloadFiles, purgeFiles, renameFile, restoreFiles, trashFiles } from './cloudActions.js';
import { KIND_COLORS, dateText, durationText, plural, roomColor, roomInitials, roomTargets, splitName, trashDaysLeft, actionsFor } from './cloudModel.js';
import { useCloud } from './cloudStore.js';
import { useCloudUi } from './cloudUi.js';
import { applyInRoom } from './inRoom.js';

export function CloudDetails({ file, onClose }: { file: DeskFile; onClose: () => void }) {
  const t = useT();
  const ui = useCloudUi();
  const language = useLanguage((s) => s.language);
  const summary = useCloud((s) => s.summary);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(file.name);
  const [useRoom, setUseRoom] = useState<string | null>(null);
  const { stem, ext } = splitName(file.name);
  const trashed = file.trashedAt !== null;
  const mine = file.ownerId === ui.me;
  const rooms = summary?.rooms ?? [];

  // as mesas em que dá para usar: as ligadas primeiro, depois as que a pessoa mestra
  const linked = file.rooms.map((id): DeskRoom => rooms.find((room) => room.id === id) ?? { id, name: ui.roomName(id), files: 0, members: 0, gm: false });
  const candidates = roomTargets(file, rooms, ui.roomName);
  const target = candidates.find((room) => room.id === useRoom) ?? candidates[0] ?? null;
  const actions = target ? actionsFor(file, target.gm) : [];
  const folder = summary?.folders.find((f) => f.id === file.folderId);
  const image = file.mime.startsWith('image/');
  const src = file.thumbUrl ?? (image ? file.url : null);
  const left = trashed ? trashDaysLeft(file.trashedAt!) : 0;

  const info: [string, string][] = [
    [t('cloud.details.type'), `${t(`cloud.kind.${file.kind}`)}${ext ? ` · ${ext.toUpperCase()}` : ''}`],
    [t('cloud.details.size'), formatBytes(file.size)],
    ...(file.width && file.height ? ([[t('cloud.details.dimensions'), t('cloud.details.pixels', { w: file.width, h: file.height })]] as [string, string][]) : []),
    ...(file.durationMs !== null ? ([[t('cloud.details.duration'), durationText(file.durationMs)]] as [string, string][]) : []),
    [t('cloud.details.folder'), folder?.name ?? '—'],
    [t('cloud.details.sent'), dateText(t, file.createdAt, language)],
  ];

  const commit = () => {
    setRenaming(false);
    if (name.trim() && name.trim() !== file.name) void renameFile(file, name.trim());
  };

  const panel = (
    <aside className={`rd-cloudDetails${ui.mobile ? ' is-sheet' : ''}`} aria-label={t('cloud.details')}>
      <div className="rd-cloudDetails__head">
        <span>{t('cloud.details')}</span>
        <button type="button" className="rd-iconBtn" aria-label={t('cloud.details.close')} onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <button type="button" className={`rd-cloudDetails__preview rd-cloudThumb--${file.kind}`} aria-label={t('cloud.details.preview', { name: file.name })} onClick={() => ui.openFile(file)}>
        {src ? (
          <img src={serverUrl(src)} alt="" />
        ) : (
          <Icon name={file.kind === 'audio' ? 'music' : file.kind === 'doc' ? 'sheet' : 'file'} size={44} strokeWidth={1.3} />
        )}
        <span className="rd-cloudDetails__open">
          <Icon name="maximize" size={14} />
          {t('cloud.details.open')}
        </span>
      </button>
      <div className="rd-cloudDetails__title">
        {renaming ? (
          <div className="rd-cloudDetails__rename">
            <label className="rd-cloudSr" htmlFor="rd-cloud-rename">
              {t('cloud.details.newName')}
            </label>
            <input
              id="rd-cloud-rename"
              value={name}
              maxLength={200}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setRenaming(false);
                  setName(file.name);
                }
              }}
              onBlur={commit}
            />
          </div>
        ) : (
          <h2 className="rd-cloudDetails__name">
            {stem}
            {ext ? <span className="rd-cloudCard__ext">.{ext}</span> : null}
            {mine && !trashed ? (
              <button type="button" className="rd-iconBtn" aria-label={t('cloud.details.rename')} title={t('cloud.details.rename')} onClick={() => setRenaming(true)}>
                <Icon name="edit" size={14} />
              </button>
            ) : null}
          </h2>
        )}
        <p className="rd-cloudDetails__meta">
          <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
          {t(`cloud.kind.${file.kind}`)} · {formatBytes(file.size)}
          {file.width && file.height ? ` · ${t('cloud.details.pixels', { w: file.width, h: file.height })}` : ''}
          {file.durationMs !== null ? ` · ${durationText(file.durationMs)}` : ''}
        </p>
      </div>

      {trashed ? (
        <div className="rd-cloudDetails__trash">
          <span>{left === 0 ? t('cloud.details.trashToday') : plural(t, 'cloud.details.trashLeft', left)}</span>
          <Button variant="action" onClick={() => void restoreFiles([file])}>
            {t('cloud.menu.restore')}
          </Button>
          <Button variant="secondary" className="is-danger" onClick={() => void purgeFiles([file])}>
            {t('cloud.menu.purge')}
          </Button>
        </div>
      ) : (
        <>
          <div className="rd-cloudDetails__actions">
            <Button variant="secondary" icon="maximize" onClick={() => ui.openFile(file)}>
              {t('cloud.details.open')}
            </Button>
            <Button variant="secondary" icon="download" onClick={() => void downloadFiles([file])}>
              {t('cloud.menu.download')}
            </Button>
            <Button variant="secondary" icon="link" onClick={() => void copyLink(file)}>
              {t('cloud.menu.copyLink')}
            </Button>
            {mine ? (
              <Button variant="secondary" icon="trash" onClick={() => void trashFiles([file])}>
                {t('cloud.view.trash')}
              </Button>
            ) : null}
          </div>

          <section className="rd-cloudDetails__section" aria-labelledby="rd-cloud-use">
            <div className="rd-cloudDetails__sectionHead">
              <h3 id="rd-cloud-use">{t('cloud.details.use')}</h3>
              {candidates.length > 1 && target ? (
                <label className="rd-cloudDetails__in">
                  {t('cloud.details.useIn')}
                  <ComboBox value={target.id} onChange={(id: string) => setUseRoom(id)} aria-label={t('cloud.details.use')} options={candidates.map((room) => ({ value: room.id, label: room.name }))} />
                </label>
              ) : target ? (
                <span className="rd-cloudDetails__in">
                  {t('cloud.details.useIn')} {target.name}
                </span>
              ) : null}
            </div>
            {target && actions.length ? (
              <div className="rd-cloudDetails__use">
                {actions.map((action, i) => (
                  <Button key={action} variant={i === 0 ? 'action' : 'secondary'} onClick={() => void applyInRoom(file, action, target.id)}>
                    {t(`cloud.use.${action}`)}
                  </Button>
                ))}
              </div>
            ) : (
              <>
                <p className="rd-cloudDetails__hint">{file.rooms.length ? t('cloud.details.useNone') : t('cloud.details.noRoom')}</p>
                {mine && rooms.some((room) => room.gm) ? (
                  <Button variant="action" onClick={() => ui.actions.share([file])}>
                    {t('cloud.details.link')}
                  </Button>
                ) : null}
              </>
            )}
          </section>

          <section className="rd-cloudDetails__section" aria-labelledby="rd-cloud-access">
            <div className="rd-cloudDetails__sectionHead">
              <h3 id="rd-cloud-access">{t('cloud.details.access')}</h3>
              {mine ? (
                <button type="button" className="rd-cloudLink" onClick={() => ui.actions.share([file])}>
                  {t('cloud.details.manage')}
                </button>
              ) : null}
            </div>
            <ul className="rd-cloudPeople">
              <li>
                <span className="rd-cloudPeople__avatar" aria-hidden="true">
                  <Icon name="user" size={14} />
                </span>
                <span className="rd-cloudPeople__text">
                  <span>{mine ? t('cloud.details.you') : t('cloud.details.owner')}</span>
                </span>
                <span className="rd-cloudPeople__role">{t('cloud.details.owner')}</span>
              </li>
              {linked.map((room) => (
                <li key={room.id}>
                  <span className="rd-cloudNav__room" style={{ background: roomColor(room.id) }} aria-hidden="true">
                    {roomInitials(room.name)}
                  </span>
                  <span className="rd-cloudPeople__text">
                    <span>{room.name}</span>
                    <span className="rd-cloudPeople__sub">{plural(t, 'cloud.details.players', room.members)}</span>
                  </span>
                  <span className="rd-cloudPeople__role">{t('cloud.details.roomLabel')}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <section className="rd-cloudDetails__section" aria-labelledby="rd-cloud-info">
        <h3 id="rd-cloud-info">{t('cloud.details.info')}</h3>
        <dl className="rd-cloudInfo">
          {info.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
    </aside>
  );

  if (!ui.mobile) return panel;
  return (
    <div className="rd-cloudSheet" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {panel}
    </div>
  );
}
